/**
 * Secure token storage utility with expiration and validation
 */

interface TokenData {
    token: string;
    expiry: number;
    issued: number;
}

class SecureStorage {
    private static readonly TOKEN_PREFIX = 'secure_';
    private static readonly CUSTOMER_TOKEN_KEY = 'customerToken';
    // private static readonly ADMIN_TOKEN_KEY = 'Token';
    
    /**
     * Set customer token with automatic expiration
     */
    static setCustomerToken(token: string, expiryHours: number = 24): void {
        const tokenData: TokenData = {
            token: token.startsWith('Bearer ') ? token : `Bearer ${token}`,
            expiry: Date.now() + (expiryHours * 60 * 60 * 1000),
            issued: Date.now()
        };
        
        try {
            const encrypted = this.simpleEncrypt(JSON.stringify(tokenData));
            localStorage.setItem(this.TOKEN_PREFIX + this.CUSTOMER_TOKEN_KEY, encrypted);
        } catch (error) {
            console.error('Failed to store customer token:', error);
            // Fallback to plain storage if encryption fails
            localStorage.setItem(this.CUSTOMER_TOKEN_KEY, token);
        }
    }
    
    /**
     * Get customer token with automatic validation and cleanup
     */
    static getCustomerToken(): string | null {
        try {
            // Try secure storage first
            const encrypted = localStorage.getItem(this.TOKEN_PREFIX + this.CUSTOMER_TOKEN_KEY);
            if (encrypted) {
                const decrypted = this.simpleDecrypt(encrypted);
                const tokenData: TokenData = JSON.parse(decrypted);
                
                // Check if token is expired
                if (Date.now() > tokenData.expiry) {
                    this.removeCustomerToken();
                    return null;
                }
                
                // Check if token is suspiciously old (over 7 days)
                const daysSinceIssued = (Date.now() - tokenData.issued) / (24 * 60 * 60 * 1000);
                if (daysSinceIssued > 7) {
                    this.removeCustomerToken();
                    return null;
                }
                
                return tokenData.token;
            }
            
            // Fallback to old storage method
            const plainToken = localStorage.getItem(this.CUSTOMER_TOKEN_KEY);
            if (plainToken) {
                // Migrate to secure storage
                this.setCustomerToken(plainToken);
                localStorage.removeItem(this.CUSTOMER_TOKEN_KEY);
                return plainToken;
            }
            
        } catch (error) {
            console.error('Failed to retrieve customer token:', error);
            // Clean up corrupted data
            this.removeCustomerToken();
        }
        
        return null;
    }
    
    /**
     * Remove customer token from all storage locations
     */
    static removeCustomerToken(): void {
        localStorage.removeItem(this.TOKEN_PREFIX + this.CUSTOMER_TOKEN_KEY);
        localStorage.removeItem(this.CUSTOMER_TOKEN_KEY);
    }
    
    /**
     * Check if customer token is valid and not expired
     */
    static isCustomerTokenValid(): boolean {
        return this.getCustomerToken() !== null;
    }
    
    /**
     * Get time until token expires (in minutes)
     */
    static getTokenExpiryTime(): number | null {
        try {
            const encrypted = localStorage.getItem(this.TOKEN_PREFIX + this.CUSTOMER_TOKEN_KEY);
            if (encrypted) {
                const decrypted = this.simpleDecrypt(encrypted);
                const tokenData: TokenData = JSON.parse(decrypted);
                return Math.max(0, Math.floor((tokenData.expiry - Date.now()) / (60 * 1000)));
            }
        } catch (error) {
            console.error('Failed to get token expiry:', error);
        }
        return null;
    }
    
    /**
     * Clean up all expired tokens from localStorage
     */
    static cleanupExpiredTokens(): void {
        const keysToCheck = Object.keys(localStorage).filter(key => 
            key.startsWith(this.TOKEN_PREFIX)
        );
        
        keysToCheck.forEach(key => {
            try {
                const encrypted = localStorage.getItem(key);
                if (encrypted) {
                    const decrypted = this.simpleDecrypt(encrypted);
                    const tokenData: TokenData = JSON.parse(decrypted);
                    
                    if (Date.now() > tokenData.expiry) {
                        localStorage.removeItem(key);
                    }
                }
            } catch (error) {
                // Remove corrupted data
                localStorage.removeItem(key);
            }
        });
    }
    
    /**
     * Simple encryption for token data (not cryptographically secure, just obfuscation)
     */
    private static simpleEncrypt(text: string): string {
        const key = this.getStorageKey();
        return btoa(text.split('').map((char, i) => 
            String.fromCharCode(char.charCodeAt(0) ^ key.charCodeAt(i % key.length))
        ).join(''));
    }
    
    /**
     * Simple decryption for token data
     */
    private static simpleDecrypt(encrypted: string): string {
        const key = this.getStorageKey();
        const text = atob(encrypted);
        return text.split('').map((char, i) => 
            String.fromCharCode(char.charCodeAt(0) ^ key.charCodeAt(i % key.length))
        ).join('');
    }
    
    /**
     * Generate a consistent key for this browser/domain
     */
    private static getStorageKey(): string {
        const userAgent = navigator.userAgent;
        const domain = window.location.hostname;
        return btoa(`${domain}-${userAgent.length}-vivaclub`).substring(0, 16);
    }
}

export default SecureStorage;
